import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  User, Lock, LogIn, LogOut, X, Eye, EyeOff,
  Mail, Phone, Building2, Calendar, Clock, Shield,
  Edit2, Save, AlertCircle, RefreshCw, ChevronDown,
  Trash2, Check, XCircle, KeyRound, Activity,
  Users, TrendingUp, MessageSquare, History, Zap, Radio, AtSign, Cookie, Settings, FileText, Download, Send, Terminal
} from 'lucide-react';
import InvoiceGenerator, { InvoicePreview } from './InvoiceGenerator';
import AdminOpsConsole from './AdminOpsConsole';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  contacted: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

// Sample AI-generated appointment for demo
const SAMPLE_BOOKING = {
  client_id: "NEURA-DEMO001",
  name: "Sarah Mitchell",
  email: "sarah.mitchell@techstartup.co.uk",
  phone: "+44 7890 123456",
  company_name: "TechStartup Solutions Ltd",
  business_category: "E-commerce Store Setup",
  notes: "Looking for a modern e-commerce website with AI chatbot integration",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  status: "confirmed",
  booking_reference: "BK-DEMO1234",
  password_changed: false,
  last_login: null,
  source: "ai_demo"
};

const ClientPortal = ({ isOpen, onClose }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [clientData, setClientData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  
  const [clientId, setClientId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Sign-up state
  const [isSignUp, setIsSignUp] = useState(false);
  const [signUpData, setSignUpData] = useState({ name: '', email: '', phone: '', company: '', password: '', confirmPassword: '' });
  const [signUpError, setSignUpError] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [adminLoading, setAdminLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // New state for traffic and history
  const [activeTab, setActiveTab] = useState('appointments'); // 'appointments', 'traffic', 'history', 'leads', 'consent'
  const [trafficData, setTrafficData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // State for leads/emails
  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  // State for cookie consent data
  const [consentData, setConsentData] = useState({ consents: [], stats: {} });
  const [consentLoading, setConsentLoading] = useState(false);
  
  // State for admin management (Super Admin only)
  const [adminsData, setAdminsData] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ client_id: '', name: '', email: '', password: '', phone: '', admin_role: 'limited_admin' });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAdminForm, setEditAdminForm] = useState({});
  const [deleteAdminConfirm, setDeleteAdminConfirm] = useState(null);
  const [resetPasswordAdmin, setResetPasswordAdmin] = useState(null);
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [adminActionError, setAdminActionError] = useState('');
  const [adminActionSuccess, setAdminActionSuccess] = useState('');

  // State for invoices
  const [invoicesData, setInvoicesData] = useState({ invoices: [], stats: {} });
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');

  // State for Agency Ops Console (Super Admin only)
  const [showOpsConsole, setShowOpsConsole] = useState(false);

  const isAdminClient = clientData?.is_admin_client === true;
  const isSuperAdmin = clientData?.admin_role === 'super_admin';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, password })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await response.json();
      setToken(data.access_token);
      setIsLoggedIn(true);
      
      await fetchProfile(data.access_token);
      await fetchBookings(data.access_token);
      
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchProfile = async (accessToken) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/me`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientData(data);
        setEditData({
          name: data.name || '',
          phone: data.phone || '',
          company_name: data.company_name || '',
          business_category: data.business_category || '',
          preferred_date: data.preferred_date || '',
          preferred_time: data.preferred_time || ''
        });

        if (data.is_admin_client) {
          await fetchAllBookings(accessToken);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const fetchBookings = async (accessToken) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/me/bookings`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
  };

  const fetchAllBookings = async (accessToken) => {
    setAdminLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/all-bookings`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out test bookings - only show real organic clients
        const realBookings = (data.bookings || []).filter(b => 
          !b.name?.toLowerCase().includes('test') &&
          !b.email?.toLowerCase().includes('test') &&
          !b.company_name?.toLowerCase().includes('test') &&
          !b.client_id?.includes('TEST') &&
          b.source !== 'ai_demo'
        );
        // Show 1 demo appointment + real organic bookings only
        setAllBookings([SAMPLE_BOOKING, ...realBookings]);
      }
    } catch (err) {
      console.error('Failed to fetch all bookings:', err);
      // Fallback to just sample booking
      setAllBookings([SAMPLE_BOOKING]);
    } finally {
      setAdminLoading(false);
    }
  };

  const deleteBooking = async (targetClientId) => {
    if (targetClientId === "NEURA-DEMO001") {
      // Demo booking - just remove from state
      setAllBookings(prev => prev.filter(b => b.client_id !== targetClientId));
      setDeleteConfirm(null);
      return;
    }
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/admin/bookings/${targetClientId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        await fetchAllBookings(token);
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Failed to delete booking:', err);
    }
  };

  const saveBookingEdit = async (targetClientId) => {
    if (targetClientId === "NEURA-DEMO001") {
      // Demo booking - just update local state
      setAllBookings(prev => prev.map(b => 
        b.client_id === targetClientId ? { ...b, ...editFormData } : b
      ));
      setEditingBooking(null);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (editFormData.name) params.append('name', editFormData.name);
      if (editFormData.phone) params.append('phone', editFormData.phone);
      if (editFormData.company_name) params.append('company_name', editFormData.company_name);
      if (editFormData.business_category) params.append('business_category', editFormData.business_category);
      if (editFormData.notes) params.append('notes', editFormData.notes);
      
      const response = await fetch(
        `${BACKEND_URL}/api/clients/admin/bookings/${targetClientId}/edit?${params}`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        await fetchAllBookings(token);
        setEditingBooking(null);
      }
    } catch (err) {
      console.error('Failed to save booking:', err);
    }
  };

  const fetchLiveTraffic = async (accessToken) => {
    setTrafficLoading(true);
    try {
      // Fetch both traffic stats and active visitors in parallel
      const [trafficRes, visitorsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/clients/admin/live-traffic`, {
          headers: { 'Authorization': `Bearer ${accessToken || token}` }
        }),
        fetch(`${BACKEND_URL}/api/visitors/active`)
      ]);
      
      if (trafficRes.ok) {
        const trafficData = await trafficRes.json();
        const visitorsData = visitorsRes.ok ? await visitorsRes.json() : { active_now: 0, visitors: [] };
        
        setTrafficData({
          ...trafficData,
          active_visitors: visitorsData
        });
      }
    } catch (err) {
      console.error('Failed to fetch traffic data:', err);
    } finally {
      setTrafficLoading(false);
    }
  };

  const fetchActivityHistory = async (accessToken) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/activity-history?limit=50`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryData(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getEventIcon = (event) => {
    switch(event) {
      case 'login_success': return <LogIn className="w-3.5 h-3.5 text-green-500" />;
      case 'login_failed': return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'account_created': return <User className="w-3.5 h-3.5 text-blue-500" />;
      case 'client_deleted': return <Trash2 className="w-3.5 h-3.5 text-red-500" />;
      case 'admin_login_success': return <Shield className="w-3.5 h-3.5 text-purple-500" />;
      case 'admin_login_failed': return <AlertCircle className="w-3.5 h-3.5 text-orange-500" />;
      default: return <Activity className="w-3.5 h-3.5 text-gray-500" />;
    }
  };

  const formatEventName = (event) => {
    return event?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const fetchLeads = async (accessToken) => {
    setLeadsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/admin/all`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeadsData(data.leads || []);
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const getSourceLabel = (source) => {
    switch(source) {
      case 'website_checklist': return 'Website Checklist';
      case 'seo_audit': return 'SEO Audit';
      case 'ai_guide_2026': return 'AI Guide';
      default: return source || 'Unknown';
    }
  };

  const fetchConsents = async (accessToken) => {
    setConsentLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/consent/admin/all`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConsentData(data);
      }
    } catch (err) {
      console.error('Failed to fetch consent records:', err);
    } finally {
      setConsentLoading(false);
    }
  };

  const getConsentActionLabel = (action) => {
    switch(action) {
      case 'accepted_all': return { label: 'Accepted All', color: 'bg-green-100 text-green-700' };
      case 'rejected_all': return { label: 'Rejected Non-Essential', color: 'bg-red-100 text-red-700' };
      case 'customized': return { label: 'Customized', color: 'bg-purple-100 text-purple-700' };
      default: return { label: action || 'Unknown', color: 'bg-gray-100 text-gray-700' };
    }
  };

  // ==================== ADMIN MANAGEMENT FUNCTIONS (Super Admin Only) ====================
  
  const fetchAdmins = async (accessToken) => {
    setAdminsLoading(true);
    setAdminActionError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/admins`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminsData(data.admins || []);
      } else if (response.status === 403) {
        setAdminActionError('Super admin access required');
      }
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setAdminsLoading(false);
    }
  };

  const createAdmin = async () => {
    setAdminActionError('');
    setAdminActionSuccess('');
    try {
      const params = new URLSearchParams({
        client_id: newAdminForm.client_id,
        name: newAdminForm.name,
        email: newAdminForm.email,
        password: newAdminForm.password,
        admin_role: newAdminForm.admin_role
      });
      if (newAdminForm.phone) params.append('phone', newAdminForm.phone);
      
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/admins/create?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setAdminActionSuccess('Admin account created successfully!');
        setShowCreateAdmin(false);
        setNewAdminForm({ client_id: '', name: '', email: '', password: '', phone: '', admin_role: 'limited_admin' });
        await fetchAdmins(token);
        setTimeout(() => setAdminActionSuccess(''), 3000);
      } else {
        const data = await response.json();
        setAdminActionError(data.detail || 'Failed to create admin');
      }
    } catch (err) {
      setAdminActionError('Failed to create admin account');
    }
  };

  const saveAdminEdit = async (adminClientId) => {
    setAdminActionError('');
    try {
      const params = new URLSearchParams();
      if (editAdminForm.name) params.append('name', editAdminForm.name);
      if (editAdminForm.email) params.append('email', editAdminForm.email);
      if (editAdminForm.phone !== undefined) params.append('phone', editAdminForm.phone || '');
      if (editAdminForm.admin_role) params.append('admin_role', editAdminForm.admin_role);
      
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/admins/${adminClientId}/edit?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setEditingAdmin(null);
        await fetchAdmins(token);
        setAdminActionSuccess('Admin updated successfully!');
        setTimeout(() => setAdminActionSuccess(''), 3000);
      } else {
        const data = await response.json();
        setAdminActionError(data.detail || 'Failed to update admin');
      }
    } catch (err) {
      setAdminActionError('Failed to update admin');
    }
  };

  const resetAdminPassword = async (adminClientId) => {
    if (!newAdminPassword || newAdminPassword.length < 6) {
      setAdminActionError('Password must be at least 6 characters');
      return;
    }
    setAdminActionError('');
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/admin/admins/${adminClientId}/reset-password?new_password=${encodeURIComponent(newAdminPassword)}`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        setResetPasswordAdmin(null);
        setNewAdminPassword('');
        setAdminActionSuccess(`Password reset for ${adminClientId}`);
        setTimeout(() => setAdminActionSuccess(''), 3000);
      } else {
        const data = await response.json();
        setAdminActionError(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setAdminActionError('Failed to reset password');
    }
  };

  const deleteAdmin = async (adminClientId) => {
    setAdminActionError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/admin/admins/${adminClientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setDeleteAdminConfirm(null);
        await fetchAdmins(token);
        setAdminActionSuccess(`Admin ${adminClientId} deleted`);
        setTimeout(() => setAdminActionSuccess(''), 3000);
      } else {
        const data = await response.json();
        setAdminActionError(data.detail || 'Failed to delete admin');
      }
    } catch (err) {
      setAdminActionError('Failed to delete admin');
    }
  };

  const getAdminRoleLabel = (role) => {
    switch(role) {
      case 'super_admin': return { label: 'Super Admin', color: 'bg-amber-100 text-amber-700' };
      case 'limited_admin': return { label: 'Limited', color: 'bg-blue-100 text-blue-700' };
      default: return { label: role || 'Unknown', color: 'bg-gray-100 text-gray-700' };
    }
  };

  // ==================== INVOICE FUNCTIONS ====================
  
  const fetchInvoices = async (accessToken) => {
    setInvoicesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${accessToken || token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoicesData(data);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchInvoices(token);
      }
    } catch (err) {
      console.error('Failed to update invoice status:', err);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchInvoices(token);
      }
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const duplicateInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchInvoices(token);
      }
    } catch (err) {
      console.error('Failed to duplicate invoice:', err);
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch(status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredInvoices = invoiceStatusFilter === 'all' 
    ? invoicesData.invoices 
    : invoicesData.invoices?.filter(inv => inv.status === invoiceStatusFilter);

  const updateBookingStatus = async (targetClientId, newStatus) => {
    setStatusUpdating(targetClientId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/admin/bookings/${targetClientId}/status?status=${newStatus}`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        await fetchAllBookings(token);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleSaveProfile = async () => {
    setSaveError('');
    setSaveSuccess('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editData)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save');
      }
      const data = await response.json();
      setClientData(data);
      setIsEditing(false);
      setSaveSuccess('Profile updated successfully!');
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setPasswordError('Password must be at least 8 characters'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/change-password?current_password=${encodeURIComponent(currentPassword)}&new_password=${encodeURIComponent(newPassword)}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) { const data = await response.json(); throw new Error(data.detail || 'Failed'); }
      setPasswordSuccess('Password changed!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setShowPasswordChange(false);
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) { setPasswordError(err.message); }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); setToken(''); setClientData(null);
    setBookings([]); setAllBookings([]); setClientId(''); setPassword(''); setIsEditing(false);
  };

  const handleClose = () => { handleLogout(); onClose(); };

  if (!isOpen) return null;

  // For admin users who are logged in, show the AdminOpsConsole directly
  if (isLoggedIn && isAdminClient) {
    return (
      <AdminOpsConsole 
        isOpen={true}
        onClose={handleClose}
        authToken={token}
        clientData={clientData}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`relative w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-cyan-600 to-purple-600">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">
              {isLoggedIn ? 'My Account' : 'Account Portal'}
            </h2>
          </div>
          <button onClick={handleClose} className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {!isLoggedIn ? (
            /* Login / Sign-up Form */
            <div className="p-6">
              {!isSignUp ? (
                /* Login Form */
                <>
                  <div className="text-center mb-6">
                    <Shield className="w-12 h-12 text-cyan-600 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">Welcome Back</h3>
                    <p className="text-sm text-gray-500 mt-1">Enter your Client ID and password to access your account</p>
                  </div>

                  {loginError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /> {loginError}
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="NEURA-XXXXXXXX" className="pl-10" required data-testid="client-id-input" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="pl-10 pr-10" required data-testid="client-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 text-white" disabled={loginLoading} data-testid="client-login-submit">
                      {loginLoading ? (
                        <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Logging in...</span>
                      ) : (
                        <span className="flex items-center gap-2"><LogIn className="w-4 h-4" />Login</span>
                      )}
                    </Button>
                  </form>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                    <p className="text-sm text-gray-600 mb-3">Don't have an account?</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                      onClick={() => { setIsSignUp(true); setLoginError(''); }}
                      data-testid="switch-to-signup"
                    >
                      <Users className="w-4 h-4 mr-2" /> Create New Account
                    </Button>
                  </div>
                </>
              ) : signUpSuccess ? (
                /* Sign-up Success */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Account Created!</h3>
                  <p className="text-sm text-gray-500 mb-6">Your Client ID has been sent to your email. Use it to login.</p>
                  <Button 
                    onClick={() => { setIsSignUp(false); setSignUpSuccess(false); }}
                    className="bg-gradient-to-r from-cyan-600 to-purple-600 text-white"
                  >
                    <LogIn className="w-4 h-4 mr-2" /> Go to Login
                  </Button>
                </div>
              ) : (
                /* Sign-up Form */
                <>
                  <div className="text-center mb-6">
                    <Users className="w-12 h-12 text-cyan-600 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">Create Account</h3>
                    <p className="text-sm text-gray-500 mt-1">Sign up to access your personalized dashboard</p>
                  </div>

                  {signUpError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /> {signUpError}
                    </div>
                  )}

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setSignUpError('');
                    
                    if (signUpData.password !== signUpData.confirmPassword) {
                      setSignUpError('Passwords do not match');
                      return;
                    }
                    if (signUpData.password.length < 4) {
                      setSignUpError('Password must be at least 4 characters');
                      return;
                    }
                    
                    setSignUpLoading(true);
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/clients/register`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: signUpData.name,
                          email: signUpData.email,
                          phone: signUpData.phone,
                          company_name: signUpData.company,
                          password: signUpData.password
                        })
                      });
                      const data = await response.json();
                      if (!response.ok) throw new Error(data.detail || 'Registration failed');
                      setSignUpSuccess(true);
                      setSignUpData({ name: '', email: '', phone: '', company: '', password: '', confirmPassword: '' });
                    } catch (err) {
                      setSignUpError(err.message);
                    } finally {
                      setSignUpLoading(false);
                    }
                  }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="text" value={signUpData.name} onChange={(e) => setSignUpData({...signUpData, name: e.target.value})} placeholder="John Smith" className="pl-10" required data-testid="signup-name" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="email" value={signUpData.email} onChange={(e) => setSignUpData({...signUpData, email: e.target.value})} placeholder="you@company.com" className="pl-10" required data-testid="signup-email" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="tel" value={signUpData.phone} onChange={(e) => setSignUpData({...signUpData, phone: e.target.value})} placeholder="+44 7XXX XXXXXX" className="pl-10" data-testid="signup-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="text" value={signUpData.company} onChange={(e) => setSignUpData({...signUpData, company: e.target.value})} placeholder="Your Company Ltd" className="pl-10" data-testid="signup-company" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="password" value={signUpData.password} onChange={(e) => setSignUpData({...signUpData, password: e.target.value})} placeholder="Min 4 characters" className="pl-10" required data-testid="signup-password" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input type="password" value={signUpData.confirmPassword} onChange={(e) => setSignUpData({...signUpData, confirmPassword: e.target.value})} placeholder="Confirm password" className="pl-10" required data-testid="signup-confirm-password" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 text-white" disabled={signUpLoading} data-testid="signup-submit">
                      {signUpLoading ? (
                        <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating Account...</span>
                      ) : (
                        <span className="flex items-center gap-2"><Users className="w-4 h-4" />Create Account</span>
                      )}
                    </Button>
                  </form>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                    <p className="text-sm text-gray-600 mb-3">Already have an account?</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                      onClick={() => { setIsSignUp(false); setSignUpError(''); }}
                      data-testid="switch-to-login"
                    >
                      <LogIn className="w-4 h-4 mr-2" /> Back to Login
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : isAdminClient ? (
            /* ADMIN CLIENT — Enhanced Dashboard with Tabs */
            <div className="p-6" data-testid="admin-client-dashboard">
              {/* Tab Navigation */}
              <div className="flex items-center gap-1 mb-6 border-b border-gray-200 -mx-6 px-6">
                <button
                  onClick={() => setActiveTab('appointments')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === 'appointments' 
                      ? 'border-cyan-500 text-cyan-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Appointments
                </button>
                <button
                  onClick={() => { setActiveTab('traffic'); fetchLiveTraffic(token); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === 'traffic' 
                      ? 'border-cyan-500 text-cyan-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-4 h-4" />
                  Live Traffic
                </button>
                <button
                  onClick={() => { setActiveTab('history'); fetchActivityHistory(token); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === 'history' 
                      ? 'border-cyan-500 text-cyan-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <History className="w-4 h-4" />
                  History
                </button>
                <button
                  onClick={() => { setActiveTab('leads'); fetchLeads(token); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === 'leads' 
                      ? 'border-cyan-500 text-cyan-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <AtSign className="w-4 h-4" />
                  Emails
                </button>
                <button
                  onClick={() => { setActiveTab('consent'); fetchConsents(token); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === 'consent' 
                      ? 'border-cyan-500 text-cyan-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Cookie className="w-4 h-4" />
                  Consent
                </button>
                {/* Admins Tab - Super Admin Only */}
                {isSuperAdmin && (
                  <button
                    onClick={() => { setActiveTab('admins'); fetchAdmins(token); }}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                      activeTab === 'admins' 
                        ? 'border-amber-500 text-amber-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Admins
                  </button>
                )}
                {/* Invoices Tab - Admin Only */}
                {isAdminClient && (
                  <button
                    onClick={() => { setActiveTab('invoices'); fetchInvoices(token); }}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                      activeTab === 'invoices' 
                        ? 'border-emerald-500 text-emerald-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Invoices
                  </button>
                )}
                {/* Agency Ops Console - Super Admin Only */}
                {isSuperAdmin && (
                  <button
                    onClick={() => {
                      console.log('Ops Console clicked, setting state to true');
                      setShowOpsConsole(true);
                    }}
                    data-testid="ops-console-btn"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 rounded-t-lg transition-all"
                  >
                    <Terminal className="w-4 h-4" />
                    Ops Console
                  </button>
                )}
              </div>

              {/* APPOINTMENTS TAB */}
              {activeTab === 'appointments' && (
              <>
              {/* Admin Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Appointment Manager</h3>
                  <p className="text-sm text-gray-500">{allBookings.length} appointments</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => fetchAllBookings(token)} disabled={adminLoading} data-testid="refresh-bookings">
                  <RefreshCw className={`w-4 h-4 mr-1 ${adminLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>

              {adminLoading && allBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                  Loading appointments...
                </div>
              ) : allBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No appointments yet</p>
                  <p className="text-sm">Bookings will appear here as clients book through the chatbot.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {allBookings.map((booking) => (
                    <div key={booking.client_id} className="border border-gray-200 rounded-xl overflow-hidden hover:border-cyan-200 transition-all" data-testid={`booking-row-${booking.client_id}`}>
                      {/* Main Row */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            {editingBooking === booking.client_id ? (
                              <Input 
                                value={editFormData.name || ''} 
                                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                                className="font-semibold mb-1"
                                placeholder="Name"
                              />
                            ) : (
                              <p className="font-semibold text-gray-900">{booking.name}</p>
                            )}
                            {editingBooking === booking.client_id ? (
                              <Input 
                                value={editFormData.company_name || ''} 
                                onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                                className="text-sm mt-1"
                                placeholder="Company"
                              />
                            ) : (
                              <p className="text-sm text-gray-500">{booking.company_name || 'No company'}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[booking.status] || STATUS_COLORS.pending}`}>
                              {booking.status}
                            </span>
                            {booking.source === 'ai_demo' && (
                              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
                                AI Demo
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Mail className="w-3.5 h-3.5 text-gray-400" /> {booking.email}
                          </div>
                          {booking.phone && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-gray-400" /> 
                              {editingBooking === booking.client_id ? (
                                <Input 
                                  value={editFormData.phone || ''} 
                                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                                  className="h-6 text-sm"
                                />
                              ) : booking.phone}
                            </div>
                          )}
                          {booking.business_category && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <Building2 className="w-3.5 h-3.5 text-gray-400" /> 
                              {editingBooking === booking.client_id ? (
                                <Input 
                                  value={editFormData.business_category || ''} 
                                  onChange={(e) => setEditFormData({...editFormData, business_category: e.target.value})}
                                  className="h-6 text-sm"
                                />
                              ) : booking.business_category}
                            </div>
                          )}
                          {booking.created_at && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <Clock className="w-3.5 h-3.5 text-gray-400" /> {new Date(booking.created_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons Row */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Status:</span>
                            {['contacted', 'completed', 'cancelled'].map((s) => (
                              <button
                                key={s}
                                onClick={() => updateBookingStatus(booking.client_id, s)}
                                disabled={statusUpdating === booking.client_id || booking.status === s}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                                  booking.status === s
                                    ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                    : 'border-gray-200 text-gray-600 hover:border-cyan-300 hover:text-cyan-700 hover:bg-cyan-50'
                                }`}
                                data-testid={`status-btn-${booking.client_id}-${s}`}
                              >
                                {statusUpdating === booking.client_id ? '...' : s}
                              </button>
                            ))}
                          </div>
                          
                          <div className="flex items-center gap-1">
                            {/* Expand/View Details */}
                            <button
                              onClick={() => setExpandedBooking(expandedBooking === booking.client_id ? null : booking.client_id)}
                              className="p-1.5 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                              title="View details"
                            >
                              <Activity className="w-4 h-4" />
                            </button>
                            
                            {/* Edit Button */}
                            {editingBooking === booking.client_id ? (
                              <>
                                <button
                                  onClick={() => saveBookingEdit(booking.client_id)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-all"
                                  title="Save"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingBooking(null)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-all"
                                  title="Cancel"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingBooking(booking.client_id);
                                  setEditFormData({
                                    name: booking.name,
                                    phone: booking.phone,
                                    company_name: booking.company_name,
                                    business_category: booking.business_category,
                                    notes: booking.notes
                                  });
                                }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Delete Button */}
                            {deleteConfirm === booking.client_id ? (
                              <>
                                <button
                                  onClick={() => deleteBooking(booking.client_id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-all"
                                  title="Confirm delete"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-all"
                                  title="Cancel"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(booking.client_id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details Panel */}
                      {expandedBooking === booking.client_id && (
                        <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Account Details</h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <KeyRound className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Client ID</p>
                                <code className="text-xs font-mono text-gray-800">{booking.client_id}</code>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Password Changed</p>
                                <p className={`text-xs font-medium ${booking.password_changed ? 'text-green-600' : 'text-amber-600'}`}>
                                  {booking.password_changed ? 'Yes' : 'No (using temp password)'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Last Login</p>
                                <p className="text-xs text-gray-800">
                                  {booking.last_login 
                                    ? new Date(booking.last_login).toLocaleString() 
                                    : 'Never logged in'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-xs text-gray-500">Created</p>
                                <p className="text-xs text-gray-800">
                                  {booking.created_at ? new Date(booking.created_at).toLocaleString() : 'Unknown'}
                                </p>
                              </div>
                            </div>
                            {booking.updated_at && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Last Updated</p>
                                  <p className="text-xs text-gray-800">{new Date(booking.updated_at).toLocaleString()}</p>
                                </div>
                              </div>
                            )}
                            {booking.booking_reference && (
                              <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Booking Ref</p>
                                  <code className="text-xs font-mono text-gray-800">{booking.booking_reference}</code>
                                </div>
                              </div>
                            )}
                          </div>
                          {booking.notes && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Notes</p>
                              {editingBooking === booking.client_id ? (
                                <textarea 
                                  value={editFormData.notes || ''} 
                                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                                  className="w-full text-sm p-2 border rounded"
                                  rows={2}
                                />
                              ) : (
                                <p className="text-sm text-gray-700">{booking.notes}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </>
              )}

              {/* LIVE TRAFFIC TAB */}
              {activeTab === 'traffic' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Live Traffic & Stats</h3>
                      <p className="text-sm text-gray-500">Real-time website activity</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchLiveTraffic(token)} disabled={trafficLoading}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${trafficLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>

                  {trafficLoading && !trafficData ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading traffic data...
                    </div>
                  ) : trafficData ? (
                    <>
                      {/* Live Visitors Banner */}
                      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 mb-6 text-white relative overflow-hidden">
                        <div className="absolute top-2 right-2">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                            <span className="text-xs font-medium opacity-90">LIVE</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                            <Radio className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold">{trafficData.active_visitors?.active_now || 0}</p>
                            <p className="text-sm opacity-90">visitors on site right now</p>
                          </div>
                          <div className="ml-auto text-right">
                            <p className="text-lg font-semibold">{trafficData.active_visitors?.active_1hour || 0}</p>
                            <p className="text-xs opacity-75">last hour</p>
                          </div>
                        </div>
                        
                        {/* Active Visitors List */}
                        {trafficData.active_visitors?.visitors?.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-white/20">
                            <p className="text-xs font-medium opacity-75 mb-2">Active Sessions:</p>
                            <div className="space-y-1.5 max-h-[100px] overflow-y-auto">
                              {trafficData.active_visitors.visitors.map((v, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs bg-white/10 rounded-lg px-3 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></span>
                                    <code className="opacity-90">{v.visitor_id}</code>
                                  </div>
                                  <span className="opacity-75">{v.page}</span>
                                  <span className="opacity-60">
                                    {v.session_duration_seconds 
                                      ? `${Math.floor(v.session_duration_seconds / 60)}m ${v.session_duration_seconds % 60}s`
                                      : 'Just arrived'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 border border-cyan-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-5 h-5 text-cyan-600" />
                            <span className="text-xs font-medium text-cyan-700">Total Clients</span>
                          </div>
                          <p className="text-2xl font-bold text-cyan-800">{trafficData.stats?.total_clients || 0}</p>
                          <p className="text-xs text-cyan-600">+{trafficData.stats?.new_clients_24h || 0} today</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-5 h-5 text-purple-600" />
                            <span className="text-xs font-medium text-purple-700">Bookings</span>
                          </div>
                          <p className="text-2xl font-bold text-purple-800">{trafficData.stats?.total_bookings || 0}</p>
                          <p className="text-xs text-purple-600">+{trafficData.stats?.bookings_24h || 0} today</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-5 h-5 text-emerald-600" />
                            <span className="text-xs font-medium text-emerald-700">Leads</span>
                          </div>
                          <p className="text-2xl font-bold text-emerald-800">{trafficData.stats?.total_leads || 0}</p>
                          <p className="text-xs text-emerald-600">+{trafficData.stats?.leads_24h || 0} today</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-5 h-5 text-amber-600" />
                            <span className="text-xs font-medium text-amber-700">Chat Sessions</span>
                          </div>
                          <p className="text-2xl font-bold text-amber-800">{trafficData.stats?.total_chats || 0}</p>
                          <p className="text-xs text-amber-600">+{trafficData.stats?.chats_24h || 0} today</p>
                        </div>
                      </div>

                      {/* 7-Day Stats */}
                      <div className="bg-gray-50 rounded-xl p-4 mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Last 7 Days
                        </h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-lg font-bold text-gray-800">{trafficData.stats?.new_clients_7d || 0}</p>
                            <p className="text-xs text-gray-500">New Clients</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-800">{trafficData.stats?.bookings_7d || 0}</p>
                            <p className="text-xs text-gray-500">Bookings</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-800">{trafficData.stats?.logins_7d || 0}</p>
                            <p className="text-xs text-gray-500">Logins</p>
                          </div>
                        </div>
                      </div>

                      {/* Recent Activity */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4" /> Recent Activity (24h)
                        </h4>
                        {trafficData.recent_activity?.length > 0 ? (
                          <div className="space-y-2 max-h-[150px] overflow-y-auto">
                            {trafficData.recent_activity.map((activity, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-100">
                                {getEventIcon(activity.event)}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{formatEventName(activity.event)}</p>
                                  <p className="text-xs text-gray-500">{activity.client_id || activity.username || 'System'}</p>
                                </div>
                                <span className="text-xs text-gray-400">
                                  {activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString() : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Click refresh to load traffic data</p>
                    </div>
                  )}
                </div>
              )}

              {/* HISTORY TAB */}
              {activeTab === 'history' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Activity History</h3>
                      <p className="text-sm text-gray-500">All system events and actions</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchActivityHistory(token)} disabled={historyLoading}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>

                  {historyLoading && historyData.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading history...
                    </div>
                  ) : historyData.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {historyData.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all">
                          <div className="mt-0.5">
                            {getEventIcon(log.event)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-800">{formatEventName(log.event)}</p>
                              {log.details && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                  {log.details}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {log.client_id || log.username || 'System'} 
                              {log.timestamp && ` • ${new Date(log.timestamp).toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No history available</p>
                    </div>
                  )}
                </div>
              )}

              {/* LEADS/EMAILS TAB */}
              {activeTab === 'leads' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Collected Emails</h3>
                      <p className="text-sm text-gray-500">{leadsData.length} emails captured</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchLeads(token)} disabled={leadsLoading}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${leadsLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>

                  {leadsLoading && leadsData.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading emails...
                    </div>
                  ) : leadsData.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {leadsData.map((lead, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-cyan-200 transition-all">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <Mail className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{lead.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                {getSourceLabel(lead.source)}
                              </span>
                              {lead.name && (
                                <span className="text-xs text-gray-500">{lead.name}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-400">
                              {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}
                            </p>
                            <p className="text-xs text-gray-400">
                              {lead.created_at ? new Date(lead.created_at).toLocaleTimeString() : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <AtSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No emails collected yet</p>
                      <p className="text-sm">Leads will appear here when visitors submit their email for free resources.</p>
                    </div>
                  )}
                </div>
              )}

              {/* CONSENT TAB */}
              {activeTab === 'consent' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Cookie Consent History</h3>
                      <p className="text-sm text-gray-500">{consentData.total || 0} consent records</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchConsents(token)} disabled={consentLoading}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${consentLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>

                  {/* Consent Stats */}
                  {consentData.stats && (
                    <div className="grid grid-cols-4 gap-3 mb-6">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-700">Accepted All</span>
                        </div>
                        <p className="text-xl font-bold text-green-800">{consentData.stats.accepted_all || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-3 border border-red-200">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-medium text-red-700">Rejected</span>
                        </div>
                        <p className="text-xl font-bold text-red-800">{consentData.stats.rejected_all || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Settings className="w-4 h-4 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">Customized</span>
                        </div>
                        <p className="text-xl font-bold text-purple-800">{consentData.stats.customized || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-3 border border-cyan-200">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-cyan-600" />
                          <span className="text-xs font-medium text-cyan-700">Accept Rate</span>
                        </div>
                        <p className="text-xl font-bold text-cyan-800">{consentData.stats.acceptance_rate || 0}%</p>
                      </div>
                    </div>
                  )}

                  {consentLoading && consentData.consents?.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading consent records...
                    </div>
                  ) : consentData.consents?.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {consentData.consents.map((consent, idx) => {
                        const actionInfo = getConsentActionLabel(consent.action);
                        return (
                          <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-cyan-200 transition-all">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                              <Cookie className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <code className="text-xs font-mono text-gray-600">{consent.visitor_id}</code>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${actionInfo.color}`}>
                                  {actionInfo.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                                {consent.consent && (
                                  <>
                                    <span className={consent.consent.analytics ? 'text-green-600' : 'text-gray-400'}>
                                      Analytics: {consent.consent.analytics ? '✓' : '✗'}
                                    </span>
                                    <span className={consent.consent.marketing ? 'text-green-600' : 'text-gray-400'}>
                                      Marketing: {consent.consent.marketing ? '✓' : '✗'}
                                    </span>
                                    <span className={consent.consent.functional ? 'text-green-600' : 'text-gray-400'}>
                                      Functional: {consent.consent.functional ? '✓' : '✗'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-gray-400">
                                {consent.created_at ? new Date(consent.created_at).toLocaleDateString() : ''}
                              </p>
                              <p className="text-xs text-gray-400">
                                {consent.created_at ? new Date(consent.created_at).toLocaleTimeString() : ''}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Cookie className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No consent records yet</p>
                      <p className="text-sm">Cookie consent data will appear here when visitors interact with the cookie banner.</p>
                    </div>
                  )}
                </div>
              )}

              {/* ADMINS TAB - Super Admin Only */}
              {activeTab === 'admins' && isSuperAdmin && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Admin Accounts</h3>
                      <p className="text-sm text-gray-500">{adminsData.length} admin accounts</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => fetchAdmins(token)} disabled={adminsLoading}>
                        <RefreshCw className={`w-4 h-4 mr-1 ${adminsLoading ? 'animate-spin' : ''}`} /> Refresh
                      </Button>
                      <Button size="sm" onClick={() => setShowCreateAdmin(true)} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                        <Users className="w-4 h-4 mr-1" /> Add Admin
                      </Button>
                    </div>
                  </div>

                  {/* Success/Error Messages */}
                  {adminActionSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" /> {adminActionSuccess}
                    </div>
                  )}
                  {adminActionError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> {adminActionError}
                    </div>
                  )}

                  {/* Create Admin Form */}
                  {showCreateAdmin && (
                    <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4 text-amber-600" /> Create New Admin Account
                      </h4>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="text-xs text-gray-600">Client ID *</label>
                          <Input 
                            value={newAdminForm.client_id}
                            onChange={(e) => setNewAdminForm({...newAdminForm, client_id: e.target.value.toUpperCase()})}
                            placeholder="e.g., ADMIN01"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Name *</label>
                          <Input 
                            value={newAdminForm.name}
                            onChange={(e) => setNewAdminForm({...newAdminForm, name: e.target.value})}
                            placeholder="Full name"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Email *</label>
                          <Input 
                            type="email"
                            value={newAdminForm.email}
                            onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})}
                            placeholder="email@example.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Password *</label>
                          <Input 
                            type="password"
                            value={newAdminForm.password}
                            onChange={(e) => setNewAdminForm({...newAdminForm, password: e.target.value})}
                            placeholder="Min 6 characters"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Phone</label>
                          <Input 
                            value={newAdminForm.phone}
                            onChange={(e) => setNewAdminForm({...newAdminForm, phone: e.target.value})}
                            placeholder="+44 7xxx xxxxxx"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Role *</label>
                          <select 
                            value={newAdminForm.admin_role}
                            onChange={(e) => setNewAdminForm({...newAdminForm, admin_role: e.target.value})}
                            className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 text-sm"
                          >
                            <option value="limited_admin">Limited Admin</option>
                            <option value="super_admin">Super Admin</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => { setShowCreateAdmin(false); setNewAdminForm({ client_id: '', name: '', email: '', password: '', phone: '', admin_role: 'limited_admin' }); }}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={createAdmin} className="bg-amber-600 text-white">
                          <Check className="w-4 h-4 mr-1" /> Create Admin
                        </Button>
                      </div>
                    </div>
                  )}

                  {adminsLoading && adminsData.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading admin accounts...
                    </div>
                  ) : adminsData.length > 0 ? (
                    <div className="space-y-3 max-h-[350px] overflow-y-auto">
                      {adminsData.map((admin) => {
                        const roleInfo = getAdminRoleLabel(admin.admin_role);
                        const isCurrentUser = admin.client_id === clientData?.client_id;
                        
                        return (
                          <div key={admin.client_id} className={`p-4 rounded-xl border transition-all ${isCurrentUser ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100 hover:border-amber-200'}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${admin.admin_role === 'super_admin' ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-blue-400 to-cyan-500'}`}>
                                  <Shield className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  {editingAdmin === admin.client_id ? (
                                    <Input 
                                      value={editAdminForm.name || ''}
                                      onChange={(e) => setEditAdminForm({...editAdminForm, name: e.target.value})}
                                      className="font-semibold h-7 text-sm"
                                    />
                                  ) : (
                                    <p className="font-semibold text-gray-900">{admin.name}</p>
                                  )}
                                  <code className="text-xs text-gray-500">{admin.client_id}</code>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${roleInfo.color}`}>
                                  {roleInfo.label}
                                </span>
                                {isCurrentUser && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">You</span>
                                )}
                              </div>
                            </div>

                            {/* Admin Details */}
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div className="flex items-center gap-1.5 text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                {editingAdmin === admin.client_id ? (
                                  <Input 
                                    value={editAdminForm.email || ''}
                                    onChange={(e) => setEditAdminForm({...editAdminForm, email: e.target.value})}
                                    className="h-6 text-xs"
                                  />
                                ) : admin.email}
                              </div>
                              <div className="flex items-center gap-1.5 text-gray-600">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                {editingAdmin === admin.client_id ? (
                                  <Input 
                                    value={editAdminForm.phone || ''}
                                    onChange={(e) => setEditAdminForm({...editAdminForm, phone: e.target.value})}
                                    className="h-6 text-xs"
                                  />
                                ) : (admin.phone || 'No phone')}
                              </div>
                            </div>

                            {/* Edit Role Dropdown */}
                            {editingAdmin === admin.client_id && !isCurrentUser && (
                              <div className="mb-3">
                                <label className="text-xs text-gray-500">Role</label>
                                <select 
                                  value={editAdminForm.admin_role || admin.admin_role}
                                  onChange={(e) => setEditAdminForm({...editAdminForm, admin_role: e.target.value})}
                                  className="mt-1 w-full h-8 px-2 rounded border border-gray-300 text-sm"
                                >
                                  <option value="limited_admin">Limited Admin</option>
                                  <option value="super_admin">Super Admin</option>
                                </select>
                              </div>
                            )}

                            {/* Reset Password */}
                            {resetPasswordAdmin === admin.client_id && (
                              <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                                <label className="text-xs text-gray-600">New Password</label>
                                <div className="flex gap-2 mt-1">
                                  <Input 
                                    type="password"
                                    value={newAdminPassword}
                                    onChange={(e) => setNewAdminPassword(e.target.value)}
                                    placeholder="Min 6 characters"
                                    className="flex-1"
                                  />
                                  <Button size="sm" onClick={() => resetAdminPassword(admin.client_id)} className="bg-blue-600 text-white">
                                    Reset
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => { setResetPasswordAdmin(null); setNewAdminPassword(''); }}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <div className="text-xs text-gray-400">
                                Created: {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'Unknown'}
                              </div>
                              
                              <div className="flex items-center gap-1">
                                {editingAdmin === admin.client_id ? (
                                  <>
                                    <button
                                      onClick={() => saveAdminEdit(admin.client_id)}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-all"
                                      title="Save"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setEditingAdmin(null)}
                                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-all"
                                      title="Cancel"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {/* Edit */}
                                    <button
                                      onClick={() => {
                                        setEditingAdmin(admin.client_id);
                                        setEditAdminForm({
                                          name: admin.name,
                                          email: admin.email,
                                          phone: admin.phone,
                                          admin_role: admin.admin_role
                                        });
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    
                                    {/* Reset Password */}
                                    <button
                                      onClick={() => setResetPasswordAdmin(admin.client_id)}
                                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all"
                                      title="Reset Password"
                                    >
                                      <KeyRound className="w-4 h-4" />
                                    </button>
                                    
                                    {/* Delete (not for self) */}
                                    {!isCurrentUser && (
                                      deleteAdminConfirm === admin.client_id ? (
                                        <>
                                          <button
                                            onClick={() => deleteAdmin(admin.client_id)}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-all"
                                            title="Confirm Delete"
                                          >
                                            <Check className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => setDeleteAdminConfirm(null)}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-all"
                                            title="Cancel"
                                          >
                                            <XCircle className="w-4 h-4" />
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => setDeleteAdminConfirm(admin.client_id)}
                                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No admin accounts found</p>
                    </div>
                  )}
                </div>
              )}

              {/* INVOICES TAB */}
              {activeTab === 'invoices' && isAdminClient && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Invoice Manager</h3>
                      <p className="text-sm text-gray-500">{invoicesData.total || 0} invoices</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => fetchInvoices(token)} disabled={invoicesLoading}>
                        <RefreshCw className={`w-4 h-4 mr-1 ${invoicesLoading ? 'animate-spin' : ''}`} /> Refresh
                      </Button>
                      <Button size="sm" onClick={() => { setEditingInvoice(null); setShowInvoiceGenerator(true); }} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                        <FileText className="w-4 h-4 mr-1" /> New Invoice
                      </Button>
                    </div>
                  </div>

                  {/* Invoice Stats */}
                  {invoicesData.stats && (
                    <div className="grid grid-cols-5 gap-3 mb-6">
                      <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-700">Total Value</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">£{(invoicesData.stats.total_value || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-3 border border-gray-200 cursor-pointer hover:border-gray-300" onClick={() => setInvoiceStatusFilter('draft')}>
                        <div className="flex items-center gap-2 mb-1">
                          <Edit2 className="w-4 h-4 text-gray-600" />
                          <span className="text-xs font-medium text-gray-700">Drafts</span>
                        </div>
                        <p className="text-lg font-bold text-gray-800">{invoicesData.stats.draft || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-3 border border-blue-200 cursor-pointer hover:border-blue-300" onClick={() => setInvoiceStatusFilter('sent')}>
                        <div className="flex items-center gap-2 mb-1">
                          <Send className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">Sent</span>
                        </div>
                        <p className="text-lg font-bold text-blue-800">{invoicesData.stats.sent || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200 cursor-pointer hover:border-green-300" onClick={() => setInvoiceStatusFilter('paid')}>
                        <div className="flex items-center gap-2 mb-1">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-700">Paid</span>
                        </div>
                        <p className="text-lg font-bold text-green-800">{invoicesData.stats.paid || 0}</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-3 border border-red-200 cursor-pointer hover:border-red-300" onClick={() => setInvoiceStatusFilter('overdue')}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-medium text-red-700">Overdue</span>
                        </div>
                        <p className="text-lg font-bold text-red-800">{invoicesData.stats.overdue || 0}</p>
                      </div>
                    </div>
                  )}

                  {/* Filter Tabs */}
                  <div className="flex gap-2 mb-4">
                    {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setInvoiceStatusFilter(status)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                          invoiceStatusFilter === status 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>

                  {invoicesLoading && (!invoicesData.invoices || invoicesData.invoices.length === 0) ? (
                    <div className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
                      Loading invoices...
                    </div>
                  ) : filteredInvoices?.length > 0 ? (
                    <div className="space-y-3 max-h-[350px] overflow-y-auto">
                      {filteredInvoices.map((invoice) => (
                        <div key={invoice.invoice_id} className="p-4 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 transition-all">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{invoice.client_company || invoice.client_name}</p>
                                <code className="text-xs text-gray-500 font-mono">{invoice.invoice_number}</code>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getInvoiceStatusColor(invoice.status)}`}>
                                {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                              </span>
                              <span className="text-lg font-bold text-gray-900">£{invoice.total_due?.toLocaleString()}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{invoice.project_title}</span>
                              <span>{invoice.line_items?.length || 0} items</span>
                              <span>{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : ''}</span>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              {/* Preview */}
                              <button
                                onClick={() => setPreviewInvoice(invoice)}
                                className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-all"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              
                              {/* Edit */}
                              <button
                                onClick={() => { setEditingInvoice(invoice); setShowInvoiceGenerator(true); }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              
                              {/* Duplicate */}
                              <button
                                onClick={() => duplicateInvoice(invoice.invoice_id)}
                                className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-all"
                                title="Duplicate"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              
                              {/* Status Actions */}
                              {invoice.status === 'draft' && (
                                <button
                                  onClick={() => updateInvoiceStatus(invoice.invoice_id, 'sent')}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                  title="Mark as Sent"
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                              {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                                <button
                                  onClick={() => updateInvoiceStatus(invoice.invoice_id, 'paid')}
                                  className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                  title="Mark as Paid"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              
                              {/* Delete - Super Admin Only */}
                              {isSuperAdmin && (
                                <button
                                  onClick={() => deleteInvoice(invoice.invoice_id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No invoices found</p>
                      <p className="text-sm mt-1">Create your first invoice to get started.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Logout */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </div>
            </div>
          ) : (
            /* REGULAR CLIENT — Profile View */
            <div className="p-6">
              {saveSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{saveSuccess}</div>}
              {saveError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{saveError}</div>}
              {passwordSuccess && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{passwordSuccess}</div>}

              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-500">Client ID:</span>
                  <code className="font-mono text-sm text-gray-800">{clientData?.client_id}</code>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Personal Information</h4>
                  {!isEditing ? (
                    <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}><Edit2 className="w-4 h-4 mr-1" />Edit</Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveProfile} className="bg-cyan-600 text-white"><Save className="w-4 h-4 mr-1" />Save</Button>
                    </div>
                  )}
                </div>

                <div className="grid gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <User className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <span className="text-xs text-gray-500">Name</span>
                      {isEditing ? <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="mt-1" /> : <p className="text-gray-900">{clientData?.name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <span className="text-xs text-gray-500">Email (cannot be changed)</span>
                      <p className="text-gray-900">{clientData?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <span className="text-xs text-gray-500">Phone</span>
                      {isEditing ? <Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="mt-1" /> : <p className="text-gray-900">{clientData?.phone || 'Not provided'}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <span className="text-xs text-gray-500">Company</span>
                      {isEditing ? <Input value={editData.company_name} onChange={(e) => setEditData({...editData, company_name: e.target.value})} className="mt-1" /> : <p className="text-gray-900">{clientData?.company_name || 'Not provided'}</p>}
                    </div>
                  </div>
                </div>

                {/* Password Change */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  {!showPasswordChange ? (
                    <Button variant="outline" className="w-full" onClick={() => setShowPasswordChange(true)}>
                      <Lock className="w-4 h-4 mr-2" />Change Password
                    </Button>
                  ) : (
                    <form onSubmit={handleChangePassword} className="space-y-3">
                      <h4 className="font-medium text-gray-900">Change Password</h4>
                      {passwordError && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{passwordError}</div>}
                      <Input type="password" placeholder="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                      <Input type="password" placeholder="New password (min 8 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                      <Input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowPasswordChange(false)}>Cancel</Button>
                        <Button type="submit" className="bg-cyan-600 text-white">Update Password</Button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Bookings */}
                {bookings.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Booking History</h4>
                    <div className="space-y-2">
                      {bookings.map((booking, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <code className="text-xs text-gray-600">{booking.booking_id}</code>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[booking.status] || STATUS_COLORS.pending}`}>{booking.status}</span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{booking.business_category}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Logout */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Generator Modal */}
      {showInvoiceGenerator && (
        <InvoiceGenerator 
          token={token}
          editInvoice={editingInvoice}
          onClose={() => { setShowInvoiceGenerator(false); setEditingInvoice(null); }}
          onSave={() => fetchInvoices(token)}
        />
      )}

      {/* Invoice Preview Modal */}
      {previewInvoice && (
        <InvoicePreview 
          invoice={previewInvoice}
          onClose={() => setPreviewInvoice(null)}
        />
      )}
    </div>
  );
};

export default ClientPortal;
